import React, { Fragment, createContext, useContext, useEffect, useState } from 'react';
import {
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  InputAdornment,
  makeStyles,
  Radio,
  Switch,
} from '@material-ui/core';
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  Save,
  Visibility,
  Restore,
  Delete,
  Cancel,
  Done,
  Add,
} from '@material-ui/icons';
import { wsGetAll, wsDownload, wsUpdate, wsPost } from 'services/WService';
import GridContainer from 'components/Grid/GridContainer';
import Button from 'components/CustomButtons/Button';
import TextField from '@material-ui/core/TextField';
import { Toasty, useToasty } from 'hooks/Toasty';
import GridItem from 'components/Grid/GridItem';
import Badge from 'components/Badge/Badge';
import moment from 'moment';
import { useModal } from 'hooks/useModal';
import RouteInfoModal from './RouteInfoModal';
import ReplaceSellerModal from './ReplaceSellerModal';
import { currency } from 'hooks/Pipe';
import { wsCreate } from 'services/WService';
import ZoneSellerTable from './components/ZoneSeller';
import RegularButton from 'components/CustomButtons/Button';
import AddZoneSellerModal from './components/AddZoneSellerModal';
import AddSellerModal from './components/AddSellerModal';
import AddProviderModal from './components/AddProviderModal';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';

const useRowStyles = makeStyles({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
});

const QuotaContext = createContext({ showNotify: undefined, setRouteModal: undefined });

function ZoneQuota() {
  const [month, setMonth] = useState(moment().format('Y-MM'));
  const [quotaZones, setQuotaZones] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [groupBySellers, setGroupBySellers] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [notify, showNotify] = useToasty();
  const [routeModal, setRouteModal] = useModal();
  const [sellerModal, setSellerModal] = useModal();
  const [addSellerModal, setAddSellerModal] = useModal();
  const [addZoneSellerModal, setAddZoneSellerModal] = useModal();
  const [addProviderModal, setAddProviderModal] = useModal();
  const [dataLoading, setDataLoading] = useState(false);
  const [dataHeader, setDataHeader] = useState({
    documents: 0,
    customers: 0,
    amount: 0,
    quota_suggested: 0,
    quota: 0,
  });

  async function getZoneQuotas() {
    let period = month.replace('-', '');
    setDataLoading(true);
    let res = await wsGetAll(`quota/zone?period=${period}`);
    setDataLoading(false);
    if (res.success) {
      let data = res.data.map((row) => ({
        ...row,
        quota_suggested: parseFloat(row.quota_suggested).toFixed(2),
        quota: parseFloat(row.quota).toFixed(2),
        quota_igv: parseFloat(row.quota_igv || 0).toFixed(2),
      }));

      let newData = data.reduce(
        (prev, quotaZone) => {
          prev.quota_suggested += parseFloat(quotaZone.quota_suggested);
          prev.quota += parseFloat(quotaZone.quota);
          return prev;
        },
        {
          quota_suggested: 0,
          quota: 0,
        },
      );

      setQuotaZones(data);
      setDataHeader({
        ...data.reduce(
          (prev, quotaZone) => {
            let consolidated = quotaZone.period_data.reduce((prev, periodData) => {
              prev.documents += periodData.documents;
              prev.customers += periodData.customers;
              prev.amount += periodData.amount;
              return prev;
            }, prev);
            return consolidated;
          },
          {
            documents: 0,
            customers: 0,
            amount: 0,
            quota_suggested: 0,
            quota: 0,
          },
        ),
        quota_suggested: newData.quota_suggested.toFixed(2),
        quota: newData.quota.toFixed(2),
      });
      setLoaded(true);
    } else {
      setLoaded(true);
      showNotify('No hay cuotas configuradas para el periodo seleccionado', 'info');
      setQuotaZones([]);
      setDataHeader({
        documents: 0,
        customers: 0,
        amount: 0,
        quota_suggested: 0,
        quota: 0,
      });
    }
  }

  async function generateQuotas() {
    setProcessing(true);
    let period = month.replace('-', '');
    setDataLoading(true);
    let res = await wsPost('quota/zone', { period });
    setDataLoading(false);
    if (res.success) {
      await getZoneQuotas();
    }
    setProcessing(false);
  }

  async function passQuotasTable() {
    let period = month.replace('-', '');
    setDataLoading(true);
    setProcessing(true);
    let res = await wsPost('quota/zone/pass-to-quota-table', {
      period,
    });
    setProcessing(false);
    setDataLoading(false);
    if (res.success) {
      showNotify(res.message);
    } else {
      showNotify(res.message, 'error');
    }
  }

  async function exportExcel() {
    setProcessing(true);

    try {
      let period = month.replace('-', '');
      setDataLoading(true);
      let res = await wsDownload(`quota/zone/${period}/export`);
      setDataLoading(false);
    } catch (ex) {
      console.error(ex);
    } finally {
      setProcessing(false);
    }
  }

  async function importExcel(e) {
    const file = e.currentTarget.files[0];
    let formData = new FormData();
    formData.append('import', file);
    let period = month.replace('-', '');
    setDataLoading(true);
    setProcessing(true);
    let response = await wsCreate(`quota/zone/${period}/import`, formData);
    if (response.success) {
      showNotify(response.message);
    } else {
      showNotify(response.message, 'error');
    }
    setProcessing(false);
    setDataLoading(false);
  }

  async function generageGoogleFormData() {
    setDataLoading(true);
    let res = await wsCreate('com/supervisor-form-data', {});
    setDataLoading(false);
    if (res.success) {
      window.alert(res.message);
    }
  }

  useEffect(() => {
    getZoneQuotas();
  }, [month]);

  return (
    <QuotaContext.Provider
      value={{
        showNotify: showNotify,
        setRouteModal: setRouteModal,
        setAddSellerModal: setAddSellerModal,
      }}
    >
      <GridContainer>
        <RouteInfoModal model={routeModal} setOpen={setRouteModal} showNotify={showNotify} />
        <ReplaceSellerModal
          month={month}
          model={sellerModal}
          setOpen={setSellerModal}
          showNotify={showNotify}
        />
        <AddSellerModal
          model={addSellerModal}
          setOpen={setAddSellerModal}
          showNotify={showNotify}
        />
        <AddZoneSellerModal
          model={addZoneSellerModal}
          setOpen={setAddZoneSellerModal}
          showNotify={showNotify}
        />
        <AddProviderModal
          model={addProviderModal}
          setOpen={setAddProviderModal}
          showNotify={showNotify}
        />
        <Toasty action={notify} />
        <GridItem xs={12}>
          <TextField
            label="Periodo"
            type="month"
            value={month}
            onInput={(e) => setMonth(e.target.value)}
            disabled={processing}
          />

          {loaded && quotaZones.length === 0 ? (
            <Button color="success" onClick={generateQuotas} disabled={processing}>
              Generar cuotas para el periodo {month}
            </Button>
          ) : (
            <>
              <Button color="success" onClick={exportExcel} disabled={processing}>
                Exportar excel
              </Button>
              <Button
                color="success"
                onClick={(e) => {
                  e.currentTarget.querySelector('input').click();
                }}
                disabled={processing}
              >
                Importar excel
                <input
                  type="file"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  style={{ display: 'none' }}
                  onChange={importExcel}
                />
              </Button>
              <Button
                color="warning"
                onClick={() => setSellerModal({ show: true })}
                disabled={processing}
              >
                Reemplazar vendedores
              </Button>
              <Button color="primary" onClick={() => passQuotasTable()} disabled={processing}>
                Confirmar cuotas
              </Button>
              <Button color="primary" onClick={() => setAddProviderModal()} disabled={processing}>
                Agregar proveedor
              </Button>
            </>
          )}
          <Button color="warning" size="sm" onClick={() => generageGoogleFormData()} disabled={processing || dataLoading}>
            Formularo Supervisor
          </Button> 
          {dataLoading && <LoadingSpinner/>}
        </GridItem>
        <GridItem xs={12} style={{ marginBottom: '20px' }}>
          {loaded && quotaZones.length !== 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">Documentos</TableCell>
                  <TableCell align="left">Clientes</TableCell>
                  <TableCell align="center">Monto</TableCell>
                  <TableCell align="center">Cuota sugerida</TableCell>
                  <TableCell align="center">Cuota</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell align="left">{dataHeader.documents}</TableCell>
                  <TableCell align="left">{dataHeader.customers}</TableCell>
                  <TableCell align="center">{currency(dataHeader.amount)}</TableCell>
                  <TableCell align="center">{currency(dataHeader.quota_suggested)}</TableCell>
                  <TableCell align="center">{currency(dataHeader.quota)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </GridItem>
        {loaded && quotaZones.length === 0 ? (
          <>{processing && <h3>Generando...</h3>}</>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} />
                  <TableCell rowSpan={2} align="left" width="10%">
                    Zona
                    <Switch
                      checked={groupBySellers}
                      onChange={(e, checked) => setGroupBySellers(checked)}
                    />
                  </TableCell>

                  {loaded &&
                    quotaZones.length > 0 &&
                    quotaZones[0].period_data.map((periodData) => {
                      return (
                        <TableCell key={periodData.id} align="center" width="1" colSpan={2}>
                          {periodData.period}
                        </TableCell>
                      );
                    })}
                  <TableCell align="center" width="200" rowSpan={2}>
                    Cuota sugerida
                  </TableCell>
                  <TableCell align="center" width="200">
                    Cuota
                  </TableCell>
                  <TableCell align="center" width="200">
                    Cuota IGV
                  </TableCell>
                </TableRow>
                {loaded && quotaZones.length > 0 && (
                  <TableRow>
                    {quotaZones[0].period_data.map((periodData) => {
                      return (
                        <Fragment key={periodData.id}>
                          <TableCell align="center">Clientes</TableCell>
                          <TableCell align="center">Monto</TableCell>
                        </Fragment>
                      );
                    })}
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {quotaZones.map((quotaZone) => {
                  return (
                    <ZoneItem
                      key={quotaZone.id}
                      quotaZone={quotaZone}
                      setQuotaZones={setQuotaZones}
                      groupBySellers={groupBySellers}
                      addZoneSellerModal={addZoneSellerModal}
                      setAddZoneSellerModal={setAddZoneSellerModal}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </GridContainer>
    </QuotaContext.Provider>
  );
}

function ZoneItem(props) {
  const { quotaZone, setQuotaZones, groupBySellers } = props;
  const { addZoneSellerModal, setAddZoneSellerModal } = props;
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [quota, setQuota] = useState(quotaZone.quota);
  const [quotaIgv, setQuotaIgv] = useState(quotaZone.quota_igv || 0);
  const [originalQuotaZone, setOriginalQuotaZone] = useState(quotaZone);
  const [providerQuotas, setProviderQuotas] = useState([]);
  const { showNotify } = useContext(QuotaContext);
  const classes = useRowStyles();

  function resetZoneQuota(zoneQuota) {
    let quota = parseFloat(zoneQuota.quota).toFixed(2);
    let quotaIgv = parseFloat(zoneQuota.quota_igv || 0).toFixed(2);
    setQuotaZones((quotaZones) => {
      let newQuotaZones = [...quotaZones];
      let index = newQuotaZones.findIndex((quotaZone) => quotaZone.id === zoneQuota.id);
      newQuotaZones[index].quota = quota;
      newQuotaZones[index].quota_igv = quotaIgv;
      return newQuotaZones;
    });
    setOriginalQuotaZone((originalQuota) => ({
      ...originalQuota,
      quota: quota,
      quota_igv: quotaIgv,
    }));
    setQuota(quota);
    setQuotaIgv(quotaIgv);
  }

  async function loadProviders() {
    let res = await wsGetAll(`quota/zone/${quotaZone.id}/provider`);
    if (res.success) {
      let data = res.data.map((row) => ({
        ...row,
        quota_suggested: parseFloat(row.quota_suggested).toFixed(2),
        quota: parseFloat(row.quota).toFixed(2),
        quota_igv: parseFloat(row.quota_igv || 0).toFixed(2),
      }));
      setProviderQuotas(data);
    }
  }

  async function updateZone() {
    let res = await wsUpdate(`quota/zone/${quotaZone.id}`, { quota, quota_igv: quotaIgv });
    if (res.success) {
      showNotify(res.message);
      setOriginalQuotaZone((originalQuota) => ({ ...originalQuota, quota, quota_igv: quotaIgv }));
    } else {
      showNotify(res.message, 'error');
    }
  }

  async function handleChange(e) {
    setProviderQuotas([]);
    setLoaded(false);
    setOpen(false);
    setQuota(e.target.value);
  }

  async function handleChangeIgv(e) {
    setProviderQuotas([]);
    setLoaded(false);
    setOpen(false);
    setQuotaIgv(e.target.value);
  }

  useEffect(() => {
    if (!open) return;
    if (loaded) return;
    setLoaded(true);
    loadProviders();
  }, [open, loaded]);

  return (
    <Fragment>
      <TableRow className={classes.root}>
        <TableCell width={50}>
          {originalQuotaZone.quota !== quota ? (
            <img src="/img/pencil.gif" width="35" />
          ) : (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen((open) => !open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        {/* Zona */}
        <TableCell align="left">
          {quotaZone.zone.description} ({quotaZone.zone.code})
        </TableCell>
        {quotaZone.period_data.map((periodData) => {
          return (
            <Fragment key={periodData.id}>
              <TableCell>{periodData.customers}</TableCell>
              <TableCell>{currency(periodData.amount)}</TableCell>
            </Fragment>
          );
        })}
        {/* Cuota sugerida */}
        <TableCell align="right" width={200}>
          <TextField value={quotaZone.quota_suggested} disabled />
        </TableCell>
        {/* Cuota */}
        <TableCell align="right" width={200}>
          <TextField
            value={quota}
            onInput={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={updateZone} disabled={originalQuotaZone.quota === quota}>
                    <Save
                      style={{ color: originalQuotaZone.quota === quota ? undefined : '#4caf50' }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
        <TableCell align="right" width={200}>
          <TextField
            value={quotaIgv}
            onInput={handleChangeIgv}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={updateZone} disabled={originalQuotaZone.quota_igv === quotaIgv}>
                    <Save
                      style={{ color: originalQuotaZone.quota_igv === quotaIgv ? undefined : '#4caf50' }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        {groupBySellers ? (
          <ZoneSellerTable
            quotaZone={quotaZone}
            open={open}
            resetZoneQuota={resetZoneQuota}
            showNotify={showNotify}
            addZoneSellerModal={addZoneSellerModal}
            setAddZoneSellerModal={setAddZoneSellerModal}
          />
        ) : (
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={100}>
            <Collapse in={open} timeout="auto">
              <Box sx={{ margin: 1, marginLeft: '30px' }}>
                <Typography variant="h6" gutterBottom component="div">
                  {loaded && providerQuotas.length === 0 ? 'Cargando...' : 'Cuota proveedores'}
                </Typography>

                {loaded && providerQuotas.length > 0 && (
                  <TableContainer component={Paper}>
                    <Table size="small" aria-label="purchases" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell align="left">Proveedor</TableCell>
                          <TableCell>Cuota sugerida</TableCell>
                          <TableCell>Cuota</TableCell>
                          <TableCell>Cuota IGV</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {providerQuotas.map((quotaProvider) => {
                          return (
                            <ProviderItem
                              key={quotaProvider.id}
                              quotaProvider={quotaProvider}
                              setProviderQuotas={setProviderQuotas}
                              resetZoneQuota={resetZoneQuota}
                            />
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Collapse>
          </TableCell>
        )}
      </TableRow>
    </Fragment>
  );
}

function ProviderItem(props) {
  const { quotaProvider, setProviderQuotas, resetZoneQuota } = props;
  const [originalQuotaProvider, setOriginalQuotaProvider] = useState(quotaProvider);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [routeQuotas, setRouteQuotas] = useState([]);
  const [quota, setQuota] = useState(quotaProvider.quota);
  const [quotaIgv, setQuotaIgv] = useState(quotaProvider.quota_igv);
  const { showNotify } = useContext(QuotaContext);
  const classes = useRowStyles();

  async function loadRoutes() {
    let res = await wsGetAll(`quota/provider/${quotaProvider.id}/route`);
    if (res.success) {
      let data = res.data.map((row) => ({
        ...row,
        quota_suggested: parseFloat(row.quota_suggested).toFixed(2),
        quota: parseFloat(row.quota).toFixed(2),
        quota_igv: parseFloat(row.quota_igv).toFixed(2),
      }));
      setRouteQuotas(data);
    }
  }

  async function updateProvider() {
    let res = await wsUpdate(
      `quota/zone/${quotaProvider.quota_zone_id}/provider/${quotaProvider.id}`,
      { quota, quota_igv: quotaIgv },
    );

    if (res.success) {
      showNotify(res.message);
      setOriginalQuotaProvider((originalQuota) => ({
        ...originalQuota,
        quota: quota,
        quota_igv: quotaIgv,
      }));
      resetZoneQuota(res.data.zone_quota);
    } else {
      showNotify(res.message, 'error');
    }
  }

  function resetProviderQuota(providerQuota) {
    let quota = parseFloat(providerQuota.quota).toFixed(2);
    let quotaIgv = parseFloat(providerQuota.quota_igv).toFixed(2);
    setQuota(quota);
    setQuotaIgv(quotaIgv);
    setOriginalQuotaProvider((originalQuota) => ({ ...originalQuota, quota, quota_igv: quotaIgv }));
    setProviderQuotas((providerQuotas) => {
      let newProviderQuotas = [...providerQuotas];
      let index = newProviderQuotas.findIndex(
        (providerQuota) => providerQuota.id === providerQuota.id,
      );
      newProviderQuotas[index].quota = quota;
      newProviderQuotas[index].quota_igv = quotaIgv;
      return newProviderQuotas;
    });
  }

  async function handleChange(e) {
    setRouteQuotas([]);
    setLoaded(false);
    setOpen(false);
    setQuota(e.target.value);
  }

  async function handleChangeIgv(e) {
    setRouteQuotas([]);
    setLoaded(false);
    setOpen(false);
    setQuotaIgv(e.target.value);
  }

  useEffect(() => {
    if (!open) return;
    if (loaded) return;
    setLoaded(true);
    loadRoutes();
  }, [open, loaded]);

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell width={50}>
          {originalQuotaProvider.quota !== quota ? (
            <img src="/img/pencil.gif" width="35" />
          ) : (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen((open) => !open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell align="left">
          {quotaProvider.provider.CardName} ({quotaProvider.provider.CardCode})
        </TableCell>
        <TableCell width={200}>
          <TextField value={originalQuotaProvider.quota_suggested} disabled />
        </TableCell>
        <TableCell width={200}>
          <TextField
            value={quota}
            onInput={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={updateProvider}
                    disabled={originalQuotaProvider.quota === quota}
                  >
                    <Save
                      style={{
                        color: originalQuotaProvider.quota === quota ? undefined : '#4caf50',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
        <TableCell width={200}>
          <TextField
            value={quotaIgv}
            onInput={handleChangeIgv}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={updateProvider}
                    disabled={originalQuotaProvider.quota_igv === quotaIgv}
                  >
                    <Save
                      style={{
                        color: originalQuotaProvider.quota_igv === quotaIgv ? undefined : '#4caf50',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto">
            <Box sx={{ margin: 1, marginLeft: '30px' }}>
              <Typography variant="h6" gutterBottom component="div">
                {loaded && routeQuotas.length === 0 ? 'Cargando...' : 'Cuota rutas'}
              </Typography>

              {loaded && routeQuotas.length > 0 && (
                <TableContainer component={Paper} style={{ maxHeight: '600px' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell align="left">Ruta</TableCell>
                        <TableCell align="left">Marca</TableCell>
                        <TableCell>Cuota sugerida</TableCell>
                        <TableCell>Cuota</TableCell>
                        <TableCell>Cuota IGV</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {routeQuotas.map((routeQuota) => {
                        return (
                          <RouteItem
                            key={routeQuota.id}
                            routeQuota={routeQuota}
                            setRouteQuotas={setRouteQuotas}
                            resetProviderQuota={resetProviderQuota}
                            resetZoneQuota={resetZoneQuota}
                          />
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function RouteItem(props) {
  const { routeQuota, setRouteQuotas } = props;
  const { resetProviderQuota, resetZoneQuota } = props;
  const [originalRouteQuota, setOriginalRouteQuota] = useState(routeQuota);
  const [quota, setQuota] = useState(routeQuota.quota);
  const [quotaIgv, setQuotaIgv] = useState(routeQuota.quota_igv);
  const { showNotify, setRouteModal, setAddSellerModal } = useContext(QuotaContext);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [sellerQuotas, setSellerQuotas] = useState([]);
  const [invalid, setInvalid] = useState(false);
  const classes = useRowStyles();

  const [selectedSeller, setSelectedSeller] = useState();
  const [destinySellerId, setDestinySellerId] = useState();

  function resetRouteQuota(routeQuota) {
    let quota = parseFloat(routeQuota.quota).toFixed(2);
    setRouteQuotas((routeQuotas) => {
      let newRouteQuotas = [...routeQuotas];
      let index = newRouteQuotas.findIndex((quotaRoute) => quotaRoute.id === routeQuota.id);
      newRouteQuotas[index].quota = quota;
      return newRouteQuotas;
    });
    setOriginalRouteQuota((originalQuota) => ({
      ...originalQuota,
      quota: quota,
    }));
    setQuota(quota);
  }

  async function loadSellers() {
    let res = await wsGetAll(`quota/route/${routeQuota.id}/seller`);
    if (res.success) {
      let data = res.data.map((row) => ({
        ...row,
        quota_suggested: parseFloat(row.quota_suggested).toFixed(2),
        quota: parseFloat(row.quota).toFixed(2),
        quota_igv: parseFloat(row.quota_igv || 0).toFixed(2),
      }));
      setSellerQuotas(data);
      setInvalid(false);
      if (data.length === 0) {
        setInvalid(true);
      }
    } else {
      setInvalid(true);
    }
  }

  async function updateRoute() {
    let res = await wsUpdate(
      `quota/provider/${routeQuota.quota_provider_id}/route/${routeQuota.id}`,
      { quota, quota_igv: quotaIgv },
    );

    if (res.success) {
      setOriginalRouteQuota((quotaData) => ({ ...quotaData, quota, quota_igv: quotaIgv }));
      showNotify(res.message);

      // Cuota de proveedor
      resetProviderQuota(res.data.provider_quota);
      resetZoneQuota(res.data.zone_quota);
    } else {
      showNotify(res.message, 'error');
    }
  }

  async function restoreRoute() {
    let res = await wsUpdate(
      `quota/provider/${routeQuota.quota_provider_id}/route/${routeQuota.id}/restore`,
    );

    if (res.success) {
      showNotify(res.message);
      if (loaded) {
        setSellerQuotas(res.data);
      }
    } else {
      showNotify(res.message, 'error');
    }
  }

  async function handleChange(e) {
    setSellerQuotas([]);
    setLoaded(false);
    setOpen(false);
    setQuota(e.target.value);
  }

  async function handleChangeIgv(e) {
    setSellerQuotas([]);
    setLoaded(false);
    setOpen(false);
    setQuotaIgv(e.target.value);
  }

  async function deleteSeller(sellerQuota) {
    if (selectedSeller === undefined || destinySellerId === undefined) {
      return showNotify('Seleccione un vendedor destino', 'info');
    }

    let res = await wsPost(
      `quota/route/${sellerQuota.quota_route_id}/seller/${sellerQuota.id}/delete`,
      {
        selectedSellerId: selectedSeller.id,
        destinySellerId,
      },
    );

    if (res.success) {
      showNotify(res.message);
      setDestinySellerId(undefined);
      setSelectedSeller(undefined);
      let data = res.data.map((row) => ({
        ...row,
        quota_suggested: parseFloat(row.quota_suggested).toFixed(2),
        quota: parseFloat(row.quota).toFixed(2),
        quota_igv: parseFloat(row.quota_igv || 0).toFixed(2),
      }));
      setSellerQuotas(data);
    } else {
      showNotify(res.message, 'error');
    }
  }

  useEffect(() => {
    if (!open) return;
    if (loaded) return;
    setLoaded(true);
    loadSellers();
  }, [open, loaded]);

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell width={50}>
          {originalRouteQuota.quota !== quota ? (
            <img src="/img/pencil.gif" width="35" />
          ) : (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen((open) => !open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell align="left">
          {routeQuota.route.description} ({routeQuota.route.code})
          <Button
            justIcon
            simple
            round
            color="primary"
            onClick={() => setRouteModal({ open: true, routeQuota: routeQuota })}
          >
            <Visibility />
          </Button>
          <Button justIcon simple round color="warning" onClick={() => restoreRoute()}>
            <Restore />
          </Button>
        </TableCell>
        <TableCell align="left">
          {routeQuota.mark.description} ({routeQuota.mark.code})
        </TableCell>
        <TableCell width={200}>
          <TextField value={routeQuota.quota_suggested} disabled />
        </TableCell>
        <TableCell width={200}>
          <TextField
            value={quota}
            onInput={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={updateRoute} disabled={originalRouteQuota.quota === quota}>
                    <Save
                      style={{ color: originalRouteQuota.quota === quota ? undefined : '#4caf50' }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
        <TableCell width={200}>
          <TextField
            value={quotaIgv}
            onInput={handleChangeIgv}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={updateRoute} disabled={originalRouteQuota.quota_igv === quotaIgv}>
                    <Save
                      style={{ color: originalRouteQuota.quota_igv === quotaIgv ? undefined : '#4caf50' }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto">
            <Box sx={{ margin: 1, marginLeft: '30px' }}>
              <GridContainer>
                <GridItem xs={12}>
                  <Typography variant="h6" gutterBottom component="div">
                    {invalid
                      ? 'No hay registros válidos'
                      : loaded && sellerQuotas.length === 0
                      ? 'Cargando...'
                      : 'Cuota vendedores'}
                  </Typography>
                  <RegularButton
                    justIcon
                    color="success"
                    onClick={() => setAddSellerModal({ show: true, routeQuota, setSellerQuotas })}
                  >
                    <Add />
                  </RegularButton>
                </GridItem>
              </GridContainer>
              {loaded && sellerQuotas.length > 0 && (
                <TableContainer component={Paper} style={{ maxHeight: '400px' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell align="left">Vendedor</TableCell>
                        <TableCell align="left">Mesa</TableCell>
                        <TableCell>Cuota sugerida</TableCell>
                        <TableCell>Cuota</TableCell>
                        <TableCell>Cuota IGV</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sellerQuotas.map((sellerQuota) => {
                        return (
                          <SellerItem
                            key={sellerQuota.id}
                            sellerQuota={sellerQuota}
                            deleteSeller={deleteSeller}
                            sellerQuotas={sellerQuotas}
                            selectedSeller={selectedSeller}
                            setSelectedSeller={setSelectedSeller}
                            destinySellerId={destinySellerId}
                            setDestinySellerId={setDestinySellerId}
                            resetRouteQuota={resetRouteQuota}
                            resetProviderQuota={resetProviderQuota}
                            resetZoneQuota={resetZoneQuota}
                          />
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function SellerItem(props) {
  const { sellerQuota, deleteSeller, sellerQuotas } = props;
  const { resetZoneQuota, resetProviderQuota, resetRouteQuota } = props;
  const { selectedSeller, setSelectedSeller } = props;
  const { destinySellerId, setDestinySellerId } = props;
  const [originalSellerQuota, setOriginalSellerQuota] = useState(sellerQuota);
  const [quota, setQuota] = useState(sellerQuota.quota);
  const [quotaIgv, setQuotaIgv] = useState(sellerQuota.quota_igv);
  const { showNotify } = useContext(QuotaContext);

  function handleChange(e) {
    setQuota(e.target.value);
  }

  function handleChangeIgv(e) {
    setQuotaIgv(e.target.value);
  }

  async function updateSeller() {
    let res = await wsUpdate(`quota/route/${sellerQuota.quota_route_id}/seller/${sellerQuota.id}`, {
      quota,
      quota_igv: quotaIgv
    });

    if (res.success) {
      setOriginalSellerQuota((quotaData) => ({ ...quotaData, quota, quota_igv: quotaIgv }));
      resetRouteQuota(res.data.route_quota);
      resetProviderQuota(res.data.provider_quota);
      resetZoneQuota(res.data.zone_quota);
      showNotify(res.message);
    } else {
      showNotify(res.message, 'error');
    }
  }

  useEffect(() => {
    for (let quota of sellerQuotas) {
      if (originalSellerQuota.id === quota.id) {
        setOriginalSellerQuota(quota);
        setQuota(quota.quota);
        setQuotaIgv(quota.quota_igv);
      }
    }
  }, [sellerQuotas]);

  return (
    <TableRow>
      <TableCell width={50}>
        {selectedSeller !== undefined && (
          <>
            {selectedSeller.id === sellerQuota.id ? (
              <>
                <Button
                  simple
                  justIcon
                  color="success"
                  round
                  onClick={() => {
                    deleteSeller(sellerQuota);
                  }}
                >
                  <Done />
                </Button>
                <Button
                  simple
                  justIcon
                  color="danger"
                  round
                  onClick={() => {
                    setDestinySellerId(undefined);
                    setSelectedSeller(undefined);
                  }}
                >
                  <Cancel />
                </Button>
              </>
            ) : (
              <Radio
                checked={destinySellerId === sellerQuota.id}
                value={sellerQuota.id}
                onChange={(e) => {
                  setDestinySellerId(sellerQuota.id);
                }}
              />
            )}
          </>
        )}

        {selectedSeller === undefined && (
          <>
            {originalSellerQuota.quota !== quota ? (
              <img src="/img/pencil.gif" width="35" />
            ) : (
              <Button
                simple
                justIcon
                color="danger"
                round
                disabled={sellerQuotas.length < 2}
                onClick={() => {
                  setSelectedSeller(sellerQuota);
                }}
              >
                <Delete />
              </Button>
            )}
          </>
        )}
      </TableCell>
      <TableCell align="left">
        {sellerQuota.seller.SlpName} ({sellerQuota.seller.SlpCode})
        {sellerQuota.seller_active === true ? (
          <Badge color="success">Activo</Badge>
        ) : (
          sellerQuota.seller.deleted_at !== null && <Badge color="danger">Inactivo</Badge>
        )}
      </TableCell>
      <TableCell align="left">
        {sellerQuota.bureau.description} ({sellerQuota.bureau.code})
      </TableCell>
      <TableCell width={200}>
        <TextField value={sellerQuota.quota_suggested} disabled />
      </TableCell>
      <TableCell width={200}>
        <TextField
          value={quota}
          onInput={handleChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={updateSeller} disabled={originalSellerQuota.quota === quota}>
                  <Save
                    style={{ color: originalSellerQuota.quota === quota ? undefined : '#4caf50' }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </TableCell>
      <TableCell width={200}>
        <TextField
          value={quotaIgv}
          onInput={handleChangeIgv}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={updateSeller} disabled={originalSellerQuota.quota_igv === quotaIgv}>
                  <Save
                    style={{ color: originalSellerQuota.quota_igv === quotaIgv ? undefined : '#4caf50' }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </TableCell>
    </TableRow>
  );
}

export default ZoneQuota;
